/**
 * FirstPersonController: fly-style first-person controller.
 *
 * Adapted from supersplat-viewer/src/cameras/fly-controller.ts.
 * WASD moves along the camera basis (including pitch), E/Q (or Space) ascend/descend,
 * mouse controls yaw/pitch via pointer lock, sphere collision with sliding.
 * No gravity — free roam. Falls back to free flight when no collision is present.
 *
 * `move` input is a normalized-ish direction vector [strafe, up, forward]
 * (already scaled by the input layer for Shift/Ctrl); this controller multiplies
 * by `moveSpeed * dt` to produce a displacement.
 */

import { Vec3 } from 'playcanvas';

import type { Collision } from '../collision';
import { findSphereSpawn } from '../collision';
import type { CameraFrame, CameraState, CameraController } from './types';
import { DEFAULT_CONTROLLER_DAMPING, applyFrameRotation, dampAngles, setBasisOffset, setCameraBasis } from './camera-utils';
import { SpawnState } from './spawn-state';
import { SphereMover } from './sphere-mover';

/** Radius of the camera collision sphere (meters). */
const CAMERA_RADIUS = 0.2;

const forward = new Vec3();
const right = new Vec3();
const up = new Vec3();
const offset = new Vec3();
const spawnProbe = new Vec3();

class FirstPersonController implements CameraController {
    fov = 75;

    /** Movement speed in m/s. */
    moveSpeed = 4;

    /** Maximum height (meters) the player can ascend above the spawn point. */
    flyMaxHeight = 4;

    rotateDamping = DEFAULT_CONTROLLER_DAMPING;

    private _position = new Vec3();
    private _angles = new Vec3();
    private _targetAngles = new Vec3();
    private _spawn = new SpawnState();
    private _spawnY = 0;
    private _mover = new SphereMover(CAMERA_RADIUS);

    set collision(value: Collision | null) {
        this._mover.collision = value;
        this._mover.reset(this._position);
    }

    get collision(): Collision | null {
        return this._mover.collision;
    }

    onEnter(state: CameraState): void {
        this.goto(state);
        if (this.collision &&
            findSphereSpawn(this.collision, this._position.x, this._position.y, this._position.z,
                CAMERA_RADIUS, spawnProbe)) {
            this._position.copy(spawnProbe);
            this._mover.reset(this._position);
        }
        this._storeSpawn();
        this._spawnY = this._position.y;
    }

    update(deltaTime: number, inputFrame: CameraFrame, state: CameraState) {
        const { move, rotate } = inputFrame.read();

        applyFrameRotation(this._targetAngles, rotate);
        dampAngles(this._angles, this._targetAngles, this.rotateDamping, deltaTime);

        this._step(deltaTime, move);

        state.position.copy(this._position);
        state.angles.set(this._angles.x, this._angles.y, 0);
        state.fov = this.fov;
    }

    onExit(_state: CameraState): void {
        // nothing
    }

    goto(state: CameraState) {
        this._position.copy(state.position);
        this._angles.set(state.angles.x, state.angles.y, 0);
        this._targetAngles.copy(this._angles);
        this._mover.reset(this._position);
    }

    resetToSpawn(state: CameraState): boolean {
        if (!this._spawn.has) return false;
        this._spawn.restore(this._position, this._angles);
        this._targetAngles.copy(this._angles);
        this._mover.reset(this._position);
        state.position.copy(this._position);
        state.angles.copy(this._angles);
        state.fov = this.fov;
        return true;
    }

    private _storeSpawn() {
        this._spawn.store(this._position, this._angles);
    }

    private _step(dt: number, move: number[]) {
        setCameraBasis(this._angles, forward, right, up);
        const s = this.moveSpeed * dt;
        setBasisOffset(offset, move[0] * s, move[1] * s, move[2] * s, forward, right, up);
        this._mover.move(this._position, offset);

        // Clamp altitude: player cannot fly higher than flyMaxHeight above spawn.
        const maxY = this._spawnY + this.flyMaxHeight;
        if (this._position.y > maxY) this._position.y = maxY;
    }
}

export { FirstPersonController };
